import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { Panel } from "./Panel";
import { inset, squarify, type Rect } from "@/lib/treemap";
import { formatContract } from "@/lib/symbols";
import { money, moneySigned, percent } from "@/lib/format";
import { positionDayReturn, type SectorExposure } from "@/lib/sectors";
import type { ValuedPosition } from "@/lib/portfolio";

/**
 * The book as a map.
 *
 * Every position is a tile, sized by what it is worth and grouped into its
 * sector. The grid two panels down states the same exposure as a column of
 * numbers, and a column of numbers is the wrong instrument for this particular
 * question: nobody reads "42.1 / 18.3 / 11.4 / 9.8 / 8.2" and *sees* a
 * concentrated book. Area is read without arithmetic, which is the only reason
 * this is a map and not a fourth table.
 *
 * **Colour is the day's return on the position, not the price's day change.**
 * A short is a negative `qty`, so `dayPnl` already flips when the price falls
 * and one formula stays correct in both directions — the same rule the rest of
 * the app runs on. Colouring by the price's own move would paint a winning
 * short red, on the one screen a member goes to in order to understand risk.
 *
 * **Cash is a tile.** It is not exposure and it is deliberately absent from
 * `sectorBreakdown`, but a member who is 60% cash and sees a full map will
 * conclude they are fully invested. It sits in neutral panel grey, so it reads
 * as the absence of a bet rather than as a flat one.
 */

/** Where the colour ramp saturates. Past a 3% day, tiles stop getting louder. */
const DAY_RETURN_CAP = 3;

/** Enough height for a sector to carry its name under it. */
const LABEL_MIN_BLOCK = 38;
const LABEL_HEIGHT = 13;

/** Below these a tile is a colour and a tooltip, nothing more. */
const SYMBOL_MIN_W = 38;
const SYMBOL_MIN_H = 17;
const FIGURE_MIN_W = 52;
const FIGURE_MIN_H = 30;

const CASH_KEY = " cash";

interface Block {
  key: string;
  value: number;
  sector: SectorExposure | null;
}

export function ExposureMap({
  sectors,
  cash,
  selected,
  onSelect,
  note,
}: {
  sectors: SectorExposure[];
  cash: number;
  /** The sector the rest of the screen is filtered to, if any. */
  selected: string | null;
  onSelect: (sector: string | null) => void;
  /** Replaces the map when the portfolio cannot be drawn. */
  note?: string | null;
}) {
  const navigate = useNavigate();
  const [ref, size] = useElementSize<HTMLDivElement>();

  const blocks = useMemo<Block[]>(() => {
    const out: Block[] = sectors.map((sector) => ({
      key: sector.sector,
      value: sector.gross,
      sector,
    }));

    // A debit balance still occupies the member's attention, so it takes area
    // by magnitude like everything else and says what it is in the label.
    if (cash !== 0) out.push({ key: CASH_KEY, value: Math.abs(cash), sector: null });

    return out;
  }, [sectors, cash]);

  const layout = useMemo(() => {
    if (size.width <= 0 || size.height <= 0) return [];
    return squarify(blocks, { x: 0, y: 0, w: size.width, h: size.height });
  }, [blocks, size]);

  const empty = blocks.length === 0;

  return (
    <Panel
      title="Exposure map"
      // A floor, because the map has no intrinsic height: its tiles are
      // absolutely positioned, so an auto-height body would collapse to nothing
      // once the layout stacks and the row stops dictating a height. Same
      // reason EquityCurve carries one.
      className="min-h-[15rem]"
      meta={
        <span className="flex items-center gap-2 whitespace-nowrap">
          <span>Area = exposure</span>
          <span className="text-ink-faint">·</span>
          <span>Colour = today</span>
          {selected && (
            <>
              <span className="text-ink-faint">·</span>
              <button
                type="button"
                onClick={() => onSelect(null)}
                className="cursor-pointer uppercase text-accent hover:underline"
              >
                Clear {selected}
              </button>
            </>
          )}
        </span>
      }
    >
      <div ref={ref} className="relative h-full min-h-0 w-full overflow-hidden">
        {note ? (
          <Centred>{note}</Centred>
        ) : empty ? (
          <Centred>No positions yet. Press F2 to place your first trade.</Centred>
        ) : (
          layout.map((tile) => {
            const block = tile.item;
            const rect = inset(tile, 1);

            if (block.sector === null) {
              return <CashBlock key={block.key} rect={rect} cash={cash} />;
            }

            return (
              <SectorBlock
                key={block.key}
                rect={rect}
                sector={block.sector}
                dimmed={selected !== null && selected !== block.sector.sector}
                onSelect={() =>
                  onSelect(selected === block.sector!.sector ? null : block.sector!.sector)
                }
                onTrade={(symbol) => navigate("/trade", { state: { order: { symbol } } })}
              />
            );
          })
        )}
      </div>
    </Panel>
  );
}

function SectorBlock({
  rect,
  sector,
  dimmed,
  onSelect,
  onTrade,
}: {
  rect: Rect;
  sector: SectorExposure;
  dimmed: boolean;
  onSelect: () => void;
  onTrade: (symbol: string) => void;
}) {
  const labelled = rect.h >= LABEL_MIN_BLOCK;
  const body: Rect = labelled
    ? { x: 0, y: 0, w: rect.w, h: rect.h - LABEL_HEIGHT }
    : { x: 0, y: 0, w: rect.w, h: rect.h };

  const tiles = squarify(
    sector.holdings.map((holding) => ({
      key: holding.symbol,
      value: Math.abs(holding.marketValue),
      holding,
    })),
    body,
  );

  return (
    <div
      className={`absolute ${dimmed ? "opacity-30" : ""}`}
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      role="group"
      aria-label={`${sector.sector}, ${sector.positions} position${
        sector.positions === 1 ? "" : "s"
      }`}
    >
      {tiles.map((tile) => (
        <PositionTile
          key={tile.item.key}
          rect={inset(tile, 0.5)}
          holding={tile.item.holding}
          onTrade={onTrade}
        />
      ))}

      {labelled && (
        <button
          type="button"
          onClick={onSelect}
          title={`${sector.sector} — ${money(sector.gross)} gross. Click to filter the screen to it.`}
          className="absolute inset-x-0 bottom-0 flex cursor-pointer items-center justify-between gap-1.5 overflow-hidden px-1 hover:bg-panel-hi"
          style={{ height: LABEL_HEIGHT }}
        >
          <span className="label truncate">{sector.sector}</span>
          <span className="label label-ink shrink-0">{sector.weight.toFixed(0)}%</span>
        </button>
      )}
    </div>
  );
}

function PositionTile({
  rect,
  holding,
  onTrade,
}: {
  rect: Rect;
  holding: ValuedPosition;
  onTrade: (symbol: string) => void;
}) {
  const dayReturn = positionDayReturn(holding);

  /*
    The name the tile would paint, and how much tile that actually takes.

    "AAPL 16JAN26 150C" is four times the width of "AAPL", so a fixed 38px gate
    tuned for a ticker draws a contract as "AAPL 1…" — which is not a shorter
    name, it is a different and wrong one, and it reads as a rendering fault
    rather than as a holding. Measuring the label that will be painted is what
    lets a contract keep its whole name on a tile that has room and fall back
    to the map's existing answer for a tile that does not: colour, area and a
    tooltip, which is what every other small tile here already is.

    A short adds its " S" marker, so it is measured too. The 7.2px is IBM Plex
    Mono's 0.6em advance at the grid's 12px, plus the tile's own padding.
  */
  const name = formatContract(holding.symbol);
  const label = name + (holding.isShort ? " S" : "");
  const labelWidth = Math.max(SYMBOL_MIN_W, 6 + label.length * 7.2);

  const showSymbol = rect.w >= labelWidth && rect.h >= SYMBOL_MIN_H;
  // The figure never appears without the name above it. That used to be
  // implied — 52px of figure is wider than 38px of ticker, and 30px taller —
  // and a label gate that varies with the label breaks the implication: a
  // contract tile with room for the day's move but not for "AAPL 16JAN26 150C"
  // would draw a bare percentage belonging to nothing. Stated, it holds for
  // any label.
  const showFigure = showSymbol && rect.w >= FIGURE_MIN_W && rect.h >= FIGURE_MIN_H;

  return (
    <button
      type="button"
      onClick={() => onTrade(holding.symbol)}
      // The tooltip is this tile's detail panel rather than a hint, so it
      // carries both names: the one every other screen prints, and the stored
      // OCC symbol underneath it, which is what the API takes and what settles
      // the money. On a ticker the two are the same string and only one is
      // shown.
      title={[
        `${name} — ${holding.name}`,
        ...(name === holding.symbol ? [] : [holding.symbol]),
        `${holding.isShort ? "Short" : "Long"} ${money(Math.abs(holding.marketValue))}`,
        `Today ${dayReturn === null ? "unknown" : `${moneySigned(holding.dayPnl)} (${percent(dayReturn)})`}`,
        `P/L ${moneySigned(holding.pnl)} (${percent(holding.pnlPercent)})`,
      ].join("\n")}
      className={`absolute flex cursor-pointer flex-col items-center justify-center overflow-hidden leading-none ${
        holding.isShort ? "border border-loss/50" : ""
      }`}
      style={{
        left: rect.x,
        top: rect.y,
        width: rect.w,
        height: rect.h,
        ...tileBackground(dayReturn),
      }}
    >
      {showSymbol && (
        <span className="num truncate px-0.5 text-ink">
          {name}
          {holding.isShort && <span className="text-loss"> S</span>}
        </span>
      )}
      {showFigure && (
        <span className="num mt-0.5 text-[0.625rem] text-ink-dim">
          {dayReturn === null ? "—" : percent(dayReturn, 1)}
        </span>
      )}
    </button>
  );
}

function CashBlock({ rect, cash }: { rect: Rect; cash: number }) {
  const showLabel = rect.w >= SYMBOL_MIN_W && rect.h >= SYMBOL_MIN_H;

  return (
    <div
      className="absolute flex flex-col items-center justify-center overflow-hidden border border-line-hi bg-panel-hi leading-none"
      style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h }}
      title={`Cash — ${money(cash)}. Not exposure, but it is where the rest of the account is.`}
    >
      {showLabel && (
        <>
          <span className="label label-ink">{cash < 0 ? "Cash debit" : "Cash"}</span>
          {rect.h >= FIGURE_MIN_H && (
            <span className="num mt-0.5 text-[0.625rem] text-ink-faint">
              {money(Math.abs(cash))}
            </span>
          )}
        </>
      )}
    </div>
  );
}

/**
 * A tile's fill.
 *
 * The ramp bottoms out at a visible tint rather than at the panel colour, so a
 * flat position is still a tile with an edge; it tops out well short of the
 * pure token, so 12px ink stays legible on the loudest tile on the screen. Both
 * ends are mixed against `--color-panel`, which is what keeps the map in the
 * same material as everything around it.
 */
function tileBackground(dayReturn: number | null): CSSProperties {
  if (dayReturn === null) return { background: "var(--color-panel-hi)" };

  const magnitude = Math.min(Math.abs(dayReturn), DAY_RETURN_CAP) / DAY_RETURN_CAP;
  const mix = 10 + magnitude * 50;
  const hue = dayReturn < 0 ? "var(--color-loss)" : "var(--color-gain)";

  return { background: `color-mix(in oklab, ${hue} ${mix}%, var(--color-panel))` };
}

function Centred({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full items-center justify-center px-4 text-center text-ink-faint">
      {children}
    </div>
  );
}

/**
 * The container's pixel size.
 *
 * Squarify's whole objective is the aspect ratio of the tiles it produces, so it
 * needs the real shape of the box. Laying out on a unit square and positioning
 * in percentages would stretch every tile by the panel's own aspect ratio and
 * give back the slivers the algorithm exists to avoid.
 */
function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setSize({ width: box.width, height: box.height });
    });

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, size] as const;
}
